import { Button, Card, Input, Modal, PageHeader, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Tooltip, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import {
  CReceiver, CReceiverProvider, C_SUPPLIERS, DEFAULT_SUPPLIER, supplierCode,
} from '../../domain/usdt-providers/cReceiverProvider';
import MonoText, { MONO_FONT } from '../../core/components/MonoText';
import _module from '../configs/module';
import { WRITE_SYSTEM_SETTING } from '../configs/scopes';

/** 合併檢視:一地址一列,出款/歸集各自的 DB 列掛在下面(可能缺一邊)。 */
type MergedRow = {
  address: string;
  supplier: string;
  payout?: CReceiver;
  collect?: CReceiver;
};

const supplierOptions = C_SUPPLIERS.map((s) => ({
  value: s.address,
  label: `${s.code}（${s.address}）`,
}));

const CReceiverManager = (props: { title: string }) => {
  const canWrite = _module.can([WRITE_SYSTEM_SETTING]);
  const [tab, setTab] = useState<'main' | 'payout_c2'>('main');
  const [payoutRows, setPayoutRows] = useState<CReceiver[]>([]);
  const [collectRows, setCollectRows] = useState<CReceiver[]>([]);
  const [c2Rows, setC2Rows] = useState<CReceiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [newAddr, setNewAddr] = useState('');
  const [newSupplier, setNewSupplier] = useState(DEFAULT_SUPPLIER);
  const [editRow, setEditRow] = useState<{ address: string; ids: number[]; supplier: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, c2] = await Promise.all([
        CReceiverProvider.list('payout'),
        CReceiverProvider.list('collect'),
        CReceiverProvider.list('payout_c2'),
      ]);
      setPayoutRows(p);
      setCollectRows(c);
      setC2Rows(c2);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // 合併出款/歸集:地址為鍵,保序(出款順序優先,只在歸集出現的接在後面)
  const merged: MergedRow[] = (() => {
    const map = new Map<string, MergedRow>();
    for (const r of payoutRows) {
      map.set(r.address, { address: r.address, supplier: r.supplier ?? DEFAULT_SUPPLIER, payout: r });
    }
    for (const r of collectRows) {
      const m = map.get(r.address);
      if (m) {
        m.collect = r;
        if (!m.supplier && r.supplier) m.supplier = r.supplier;
      } else {
        map.set(r.address, { address: r.address, supplier: r.supplier ?? DEFAULT_SUPPLIER, collect: r });
      }
    }
    return Array.from(map.values());
  })();

  /**
   * 出款環的序號:只有「出款啟用」的列才佔號,停用/只做歸集的不佔。
   *
   * 這一欄是為了讓「運營錢包管理」頁的出款C游標數字對得上 —— 那個游標取模的基礎是
   * 後端的出款環(只含 enabled=1),而這張表列的是全部(含停用),兩者的位次本來就不同。
   * 順序對得起來是因為 cc 的 list 與 pj 組環的 SQL 都是 ORDER BY orderIndex, id。
   */
  const payoutSeq = (() => {
    const m = new Map<string, number>();
    let n = 0;
    for (const row of merged) {
      if (row.payout && row.payout.enabled) m.set(row.address, ++n);
    }
    return m;
  })();

  /** 開關某 type:有列改 enabled,沒列就建(接在該 type 現有 orderIndex 之後)。 */
  const toggle = async (row: MergedRow, type: 'payout' | 'collect', checked: boolean) => {
    const existing = type === 'payout' ? row.payout : row.collect;
    const list = type === 'payout' ? payoutRows : collectRows;
    try {
      if (existing) {
        await CReceiverProvider.update(existing.id, { enabled: checked });
      } else if (checked) {
        const maxOrder = list.reduce((m, r) => Math.max(m, r.orderIndex), -1);
        await CReceiverProvider.add({ type, address: row.address, orderIndex: maxOrder + 1, supplier: row.supplier });
      }
    } catch (e) {
      message.error('操作失敗,請重試');
    } finally {
      reload();
    }
  };

  const add = async () => {
    const address = newAddr.trim();
    if (!address) return;
    // 出款+歸集各建一列,皆預設啟用;已存在的那邊由後端擋重複
    const results = await Promise.allSettled([
      CReceiverProvider.add({
        type: 'payout', address, supplier: newSupplier,
        orderIndex: payoutRows.reduce((m, r) => Math.max(m, r.orderIndex), -1) + 1,
      }),
      CReceiverProvider.add({
        type: 'collect', address, supplier: newSupplier,
        orderIndex: collectRows.reduce((m, r) => Math.max(m, r.orderIndex), -1) + 1,
      }),
    ]);
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length === 2) {
      const reason: any = (failed[0] as PromiseRejectedResult).reason;
      message.error(reason?.response?.data?.message ?? '新增失敗');
    } else {
      if (failed.length === 1) message.warning('其中一邊已存在,只補了另一邊');
      else message.success('已新增(出款+歸集)');
      setNewAddr('');
    }
    reload();
  };

  const addC2 = async () => {
    const address = newAddr.trim();
    if (!address) return;
    try {
      await CReceiverProvider.add({
        type: 'payout_c2', address, supplier: newSupplier,
        orderIndex: c2Rows.reduce((m, r) => Math.max(m, r.orderIndex), -1) + 1,
      });
      setNewAddr('');
      message.success('已新增');
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? '新增失敗');
    }
    reload();
  };

  const removeIds = async (ids: number[]) => {
    try {
      for (const id of ids) await CReceiverProvider.remove(id);
    } catch (e) {
      message.error('操作失敗,請重試');
    } finally {
      reload();
    }
  };

  const saveSupplier = async () => {
    if (!editRow) return;
    try {
      for (const id of editRow.ids) {
        await CReceiverProvider.update(id, { supplier: editRow.supplier });
      }
      message.success('已更新供應商');
      setEditRow(null);
    } catch (e) {
      message.error('操作失敗,請重試');
    } finally {
      reload();
    }
  };

  const supplierCell = (supplier: string) => (
    <span>
      <Tag>{supplierCode(supplier)}</Tag>
      <MonoText style={{ color: '#999', fontSize: 12 }}>{supplier}</MonoText>
    </span>
  );

  const enabledSwitch = (row: MergedRow, type: 'payout' | 'collect') => {
    const existing = type === 'payout' ? row.payout : row.collect;
    return (
      <Switch
        checked={!!existing?.enabled}
        disabled={!canWrite}
        onChange={(checked) => toggle(row, type, checked)}
      />
    );
  };

  const addForm = (onAdd: () => void) =>
    canWrite && (
      <Space style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 380, fontFamily: MONO_FONT }}
          placeholder="TRON 地址(T 開頭 34 碼)"
          value={newAddr}
          onChange={(e) => setNewAddr(e.target.value)}
          onPressEnter={onAdd}
        />
        <Select style={{ width: 420 }} value={newSupplier} onChange={setNewSupplier} options={supplierOptions} />
        <Button type="primary" onClick={onAdd}>新增</Button>
      </Space>
    );

  return (
    <>
      <PageHeader
        title={props.title}
        subTitle="金額一律 0.01 TRX;停用=不進輪替;刪除=軟刪除;⚠️ 雙軌過渡期間,某類型清空或全部停用時系統會退回 CSV 舊名單繼續運作,不等於停用 C"
      />
      <Card>
        <Tabs activeKey={tab} onChange={(k) => setTab(k as 'main' | 'payout_c2')}>
          <Tabs.TabPane tab="C 名單(出款+歸集)" key="main" />
          <Tabs.TabPane tab="出款 C2" key="payout_c2" />
        </Tabs>

        {tab === 'main' && (
          <>
            {/* 出款C游標改 per-wallet 後,全域游標卡片已移除;各錢包的游標數字在「運營錢包管理」頁看。 */}
            {addForm(add)}
            <Table<MergedRow>
              rowKey="address"
              loading={loading}
              dataSource={merged}
              pagination={false}
              columns={[
                { title: '#', render: (_v, _r, i) => i + 1, width: 50 },
                {
                  title: '出款序',
                  width: 70,
                  align: 'center' as const,
                  render: (_v: any, r: MergedRow) => {
                    const n = payoutSeq.get(r.address);
                    return n ? (
                      <b>{n}</b>
                    ) : (
                      <Tooltip title="出款未啟用,不在出款輪替環裡">
                        <span style={{ color: '#ccc' }}>—</span>
                      </Tooltip>
                    );
                  },
                },
                { title: '地址', dataIndex: 'address', render: (v: string) => <MonoText>{v}</MonoText> },
                { title: '供應商', dataIndex: 'supplier', width: 340, render: supplierCell },
                { title: '出款啟用', width: 100, render: (_v, r) => enabledSwitch(r, 'payout') },
                { title: '歸集啟用', width: 100, render: (_v, r) => enabledSwitch(r, 'collect') },
                {
                  title: '操作', width: 150,
                  render: (_v, r) => {
                    const ids = [r.payout?.id, r.collect?.id].filter((x): x is number => x !== undefined);
                    return (
                      <Space>
                        <Button
                          size="small"
                          disabled={!canWrite || ids.length === 0}
                          onClick={() => setEditRow({ address: r.address, ids, supplier: r.supplier })}
                        >
                          編輯
                        </Button>
                        <Popconfirm
                          title={`刪除 ${r.address}?(出款+歸集一併軟刪除)`}
                          disabled={!canWrite || ids.length === 0}
                          onConfirm={() => removeIds(ids)}
                        >
                          <Button size="small" danger disabled={!canWrite || ids.length === 0}>刪除</Button>
                        </Popconfirm>
                      </Space>
                    );
                  },
                },
              ]}
            />
          </>
        )}

        {tab === 'payout_c2' && (
          <>
            {addForm(addC2)}
            <Table<CReceiver>
              rowKey="id"
              loading={loading}
              dataSource={c2Rows}
              pagination={false}
              columns={[
                { title: '#', render: (_v, _r, i) => i + 1, width: 50 },
                { title: '地址', dataIndex: 'address', render: (v: string) => <MonoText>{v}</MonoText> },
                {
                  title: '供應商', dataIndex: 'supplier', width: 340,
                  render: (v: string) => supplierCell(v ?? DEFAULT_SUPPLIER),
                },
                {
                  title: '啟用', dataIndex: 'enabled', width: 90,
                  render: (v: boolean, r) => (
                    <Switch
                      checked={v}
                      disabled={!canWrite}
                      onChange={async (checked) => {
                        try {
                          await CReceiverProvider.update(r.id, { enabled: checked });
                        } catch (e) {
                          message.error('操作失敗,請重試');
                        } finally {
                          reload();
                        }
                      }}
                    />
                  ),
                },
                {
                  title: '操作', width: 150,
                  render: (_v, r) => (
                    <Space>
                      <Button
                        size="small"
                        disabled={!canWrite}
                        onClick={() =>
                          setEditRow({ address: r.address, ids: [r.id], supplier: r.supplier ?? DEFAULT_SUPPLIER })
                        }
                      >
                        編輯
                      </Button>
                      <Popconfirm
                        title={`刪除 ${r.address}?(軟刪除)`}
                        disabled={!canWrite}
                        onConfirm={() => removeIds([r.id])}
                      >
                        <Button size="small" danger disabled={!canWrite}>刪除</Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </>
        )}
      </Card>

      <Modal
        title={`編輯供應商 — ${editRow?.address ?? ''}`}
        visible={!!editRow}
        onOk={saveSupplier}
        onCancel={() => setEditRow(null)}
        okText="儲存"
        cancelText="取消"
      >
        <Select
          style={{ width: '100%' }}
          value={editRow?.supplier}
          onChange={(v) => editRow && setEditRow({ ...editRow, supplier: v })}
          options={supplierOptions}
        />
      </Modal>
    </>
  );
};

export default CReceiverManager;
